var time_spent = (handler => {
  /**
     * https://www.igvita.com/2015/11/20/dont-lose-user-and-app-state-use-page-visibility/
     * initializes some variables such as previousUrl, isHandled, totalStartingTimeStamp
     * for when we want to use this handler.
     *
     * The `visibilitychange` DOM event is used to calculate when the user is actively on the page
     * so we can determine their active duration.
     *
     */

  // When executing on the server, return a no-op function
  if (typeof window === 'undefined') {
    return () => {};
  }

  // Events we want to look for so we can fire time_spent metrics
  // This is due to the fact that we can't determine
  // if the user bounces off the page or if the user closes the page
  // so we base the user's time duration/spent on other events that
  // get fired. Namely these set of events that get fired.
  const VALID_CET_EVENTS = ['web_internal_link', 'web_external_link'];

  // This would ensure the total starting time clock always resets after an event is sent.
  let total_starting_timestamp = Date.now();

  // This sets the active time when first initialized
  // and will be reset on subsequent invocations (like on client side route changes)
  let active_starting_timestamp = total_starting_timestamp;

  // This is used to determine how long to wait before sending the next heartbeat based
  // CET event.
  let heartbeat_event_count = 0;
  let heartbeat_start_time = Date.now();
  let current_delay = 1;
  let is_locked = false;
  // Creates the event handlers needed for calculating time spent
  const addTimeSpentListener = function () {
    for (var _len = arguments.length, additional_args = new Array(_len), _key = 0; _key < _len; _key++) {
      additional_args[_key] = arguments[_key];
    }
    // resets the starting timestamp & heartbeat count for when the user clicks on another page.
    total_starting_timestamp = Date.now();
    active_starting_timestamp = total_starting_timestamp;
    heartbeat_event_count = 0;
    current_delay = 1;
    is_locked = false;
    let animationFrame = null;

    // Gather the data needed for sending the time spent metrics.
    const sendTimeSpentMetrics = function () {
      let event = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      const current_timestamp = Date.now();
      const date_now_layer = {
        active_duration: current_timestamp - active_starting_timestamp,
        total_duration: current_timestamp - total_starting_timestamp
      };
      if (date_now_layer.total_duration > 0) {
        handler({
          layers: [date_now_layer, event]
        }, ...additional_args);
      }
      total_starting_timestamp = Date.now();
      active_starting_timestamp = Date.now();
    };

    // Sends the time spent metric when the tab is hidden
    // When the tab is visible, we reset the active starting timestamp.
    // We also set the lock for the heartbeat, so we don't send any heartbeat
    // events while the page is inactive as well as so
    // we can also resolve the race condition in which we would want to
    // set the active starting timestamp to the current timestamp
    // before sending a heartbeat event hence the lock
    // being set to false after doing so.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const event = {
          time_spent_event_type: 'page_visibility'
        };
        is_locked = true;
        sendTimeSpentMetrics(event);
        cancelAnimationFrame(animationFrame);
      } else if (document.visibilityState === 'visible') {
        active_starting_timestamp = Date.now();
        // We want to wait a second or 2 before setting the value
        setTimeout(() => is_locked = false, 2000);
        startHeartBeat(heartbeat_start_time, current_delay);
      }
    };

    // Listens to CET Events being fired and fires a time_spent metric
    // if the event is a valid cet event.
    const onCetEvent = e => {
      let data = e.detail;
      let valid_event = data.find(cet_event => VALID_CET_EVENTS.includes(cet_event.type));
      if (valid_event) {
        const event = data[0];
        active_starting_timestamp = total_starting_timestamp;

        // We could have situations where the time spent metrics could
        // be sent post page change. To solve this, we reuse the event
        // data for the time_spent event except for event_ts, event_uuid,
        // type and source
        const copied_event = {
          ...event
        };
        delete copied_event.event_ts;
        delete copied_event.event_uuid;
        delete copied_event.type;
        delete copied_event.source;

        // Add the type of time_spent here
        copied_event.time_spent_event_type = 'link_click';
        sendTimeSpentMetrics(copied_event);
      }
    };

    // Will fire a time_spent metric exponentially when the page is active
    // We pass in the start time in case we want to continue the heartbeat
    // when the page_visibility shows up since Mozilla pages don't really pause
    // requestAnimationFrame.
    // So we pass in what the heartbeat_start_time was at that moment
    // as well as what the delay was at that moment (current_delay)
    const startHeartBeat = function () {
      let start_time = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      let start_delay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
      heartbeat_start_time = start_time ? start_time : Date.now();
      function loop(delay) {
        let heartbeat_end_time = Date.now();
        let milliseconds = heartbeat_end_time - heartbeat_start_time;
        let seconds_elapsed = Math.floor(milliseconds / 1000);
        if (seconds_elapsed >= delay && !is_locked) {
          // lock the heartbeat from sending anymore events until
          // this heartbeat event is sent.
          is_locked = true;
          const event = {
            time_spent_event_type: 'heartbeat'
          };
          sendTimeSpentMetrics(event);
          setTimeout(() => is_locked = false, 1000);
          heartbeat_start_time = Date.now();
          // we want the first three events to fire after 1, 3, 5 seconds from the previous
          // event. Afterwards, the delay will grow exponentially every 2 heartbeats.
          delay = delay < 5 ? delay + 2 : 10 * Math.pow(2, Math.floor((heartbeat_event_count - 3) / 2));
          // we maintain this delay so when we restart,
          // we know what the delay time should be when we continue the heartbeat.
          current_delay = delay;
          heartbeat_event_count++;
        }
        cancelAnimationFrame(animationFrame);
        animationFrame = requestAnimationFrame(() => loop(delay));
      }
      loop(start_delay);
    };
    startHeartBeat();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('cet-event', onCetEvent);
    return () => {
      cancelAnimationFrame(animationFrame);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('cet-event', onCetEvent);
    };
  };
  return function () {
    return addTimeSpentListener(...arguments);
  };
});

export default time_spent;
